/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { SysqueSizeRangeItemsVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-size-range-vm';
import {
  FieldFabConduitSizeRangeVM,
  HandAndMachineBendSizeRangeVM,
  KickHandAndMachineBendSizeRangeVM,
} from '../../../../toolbelt/feature/src/lib/components/add-family-information/add-family-information-types';
import {
  AddFamilyInformationData,
  ServiceType,
  connectorDescription,
} from 'libs/sysque/toolbelt/feature/src/lib/models/family-information-model';
import { IFamilyInformationItem } from '../../../feature/src/lib/components/add-family-information/add-family-information-types';
import { LineGraphicsDialogConfig } from '../../../feature/src/lib/components/add-family-information/line-graphics';
import { CreateServiceTypeDialogConfig } from 'libs/sysque/toolbelt/feature/src/lib/components/add-family-information/create-service-type';
import { ImageDescriptionType } from 'libs/sysque/shared/ui/src/lib/enums/image-description-type';

//#region CableTray
export function convertCableTrayStringToSysqueSizeRangeVM(inputString: string): SysqueSizeRangeItemsVM[] {
  if (!inputString) {
    return [];
  }
  const stringRows = inputString.trim().split(',');

  const result: SysqueSizeRangeItemsVM[] = [];

  for (let j = 0; j < stringRows.length; j++) {
    const data: SysqueSizeRangeItemsVM = { id: j };
    if (stringRows[j].includes('*')) {
      data.isChecked = 0;
      stringRows[j] = stringRows[j].replace('*', '');
    } else {
      data.isChecked = 1;
    }
    const rows = stringRows[j].trim().split('x');
    const numColumns = rows.length / 2;

    for (let i = 0; i < numColumns; i++) {
      data[`Width${i + 1}`] = Number(rows[2 * i]);
      data[`Height${i + 1}`] = Number(rows[2 * i + 1]);
    }

    result.push(data);
  }

  return result;
}

export function setItem(item: AddFamilyInformationData){
  let itemToAdd = {} as IFamilyInformationItem;
  Object.assign(itemToAdd, {
    itemType : item.ItemType,
    fittingType : item.FittingType,
    shape : item.Shape,
    isHanger : item.IsHanger,
    systemName : item.SystemName,
    itemPath : decodeURIComponent(item.PathItem),
    isFromAdd : item.IsFromAdd,
    category : item.Category,
    name : item.Name,
    isFromEdit : item.IsFromEdit,
    isFromDuplicate : item.IsFromDuplicate,
    specId : item.SpecId,
    image : item.Image,
    manufacturer : item.Manufacturer,
    sizeRange : item.SizeRange,
    connectors : item.Connector,
    bendRadius : item.BendRadius,
    leadLength : item.LeadLength,
    leadOutLength : item.LeadOutLength,
    takeUp : item.TakeUp,
    id : item.Id,
    hangerAsFitting: item.HangerAsFitting
    });
    return itemToAdd;
}

export function convertCableTraySysqueSizeRangeVMToString(sysqueSizeRangeItems: SysqueSizeRangeItemsVM[]): string {
  let outputString = '';

  for (let i = 0; i < sysqueSizeRangeItems.length; i++) {
    const data = sysqueSizeRangeItems[i];

    for (let j = 2; j <= Object.keys(data).length - 1; j += 2) {
      outputString += `${data[`Width${Math.ceil(j / 2)}`]}${'x'}${data[`Height${Math.ceil(j / 2)}`]}`;

      if (j !== Object.keys(data).length - 2) {
        outputString += 'x';
      }
      if (data.isChecked === 0) {
        outputString += '*';
      }
    }

    if (i !== sysqueSizeRangeItems.length - 1) {
      outputString += ',';
    }
  }

  return outputString;
}

export function getCableTrayConnectors(): connectorDescription[] {
  return [
    {
      connector: 'Plain End',
      connectsTo: 'Female End',
      notation: 200,
    },
    {
      connector: 'Female End',
      connectsTo: 'Plain End',
      notation: 201,
    },
    {
      connector: 'Flanged End',
      connectsTo: 'Flanged End',
      notation: 202,
    },
  ] as connectorDescription[];
}

//#endregion

//#region Conduit
export function convertConduitStringToSysqueSizeRangeVM(inputString: string): SysqueSizeRangeItemsVM[] {
  const stringRows = inputString.trim().split(',');

  const result: SysqueSizeRangeItemsVM[] = [];

  for (let j = 0; j < stringRows.length; j++) {
    const data: SysqueSizeRangeItemsVM = { id: j };
    if (stringRows[j].includes('*')) {
      data.isChecked = 0;
      stringRows[j] = stringRows[j].replace('*', '');
    } else {
      data.isChecked = 1;
    }
    const rows = stringRows[j].trim().split('x');

    for (let i = 0; i < rows.length; i++) {
      data[`Diameter${i + 1}`] = Number(rows[i]);
    }

    result.push(data);
  }

  return result;
}

export function convertConduitSysqueSizeRangeVMToString(sysqueSizeRangeItems: SysqueSizeRangeItemsVM[]): string {
  let outputString = '';

  for (let i = 0; i < sysqueSizeRangeItems.length; i++) {
    const data = sysqueSizeRangeItems[i];
    let isFirstDiameter = true;

    for (const key in data) {
      if (key.startsWith('Diameter')) {
        if (!isFirstDiameter) {
          outputString += 'x';
        }
        outputString += `${data[key]}`;
        isFirstDiameter = false;
      }
    }

    if (data.isChecked === 0) {
      outputString += '*';
    }

    if (i !== sysqueSizeRangeItems.length - 1) {
      outputString += ',';
    }
  }

  return outputString;
}

export function getConduitConnectors(): connectorDescription[] {
  return [
    {
      connector: 'Male Threads',
      connectsTo: 'Female Threads',
      notation: 100,
    },
    {
      connector: 'Female Threads',
      connectsTo: 'Male Threads',
      notation: 101,
    },
    {
      connector: 'Compression Male',
      connectsTo: 'Compression Female',
      notation: 102,
    },
    {
      connector: 'Compression Female',
      connectsTo: 'Compression Male',
      notation: 103,
    },
    {
      connector: 'PVC Plain',
      connectsTo: 'PVC Socket',
      notation: 104,
    },
    {
      connector: 'PVC Socket',
      connectsTo: 'PVC Plain',
      notation: 105,
    },
    {
      connector: 'Plain',
      connectsTo: 'Bell',
      notation: 106,
    },
    {
      connector: 'Bell',
      connectsTo: 'Plain',
      notation: 107,
    },
    {
      connector: 'Knockout Male',
      connectsTo: 'Knockout Female',
      notation: 108,
    },
    {
      connector: 'Knockout Female',
      connectsTo: 'Knockout Male',
      notation: 109,
    },
  ] as connectorDescription[];
}

//#endregion

//#region FieldFab

export function convertFieldFabStringToSysqueSizeRangeVM(input: FieldFabConduitSizeRangeVM): SysqueSizeRangeItemsVM[] {
  if (!input.sizeRange) {
    return [];
  }

  const sizeRanges = input.sizeRange?.split(',');
  const bendRadii =
    input.bendRadius && input.bendRadius !== '' ? input.bendRadius.split(',') : Array(sizeRanges.length).fill('');
  const leadLengths =
    input.leadLength && input.leadLength !== '' ? input.leadLength.split(',') : Array(sizeRanges.length).fill('');
  const takeUps = input.takeUp && input.takeUp !== '' ? input.takeUp.split(',') : Array(sizeRanges.length).fill('');
  const result: SysqueSizeRangeItemsVM[] = [];

  for (let i = 0; i < sizeRanges.length; i++) {
    const diameter = parseFloat(sizeRanges[i].replace('*', '')) || 0;
    const bendRadius = parseFloat(bendRadii[i].replace('*', '')) || 0;
    const leadLength = parseFloat(leadLengths[i].replace('*', '')) || 0;
    const takeUp = parseFloat(takeUps[i].replace('*', '')) || 0;

    const isChecked = sizeRanges[i].includes('*') ? 0 : 1;

    result.push({
      id: i,
      isChecked: isChecked,
      Diameter: diameter,
      'Bend Radius': bendRadius,
      'Lead Length': leadLength,
      'Take Up': takeUp,
    });
  }

  return result;
}

export function convertSysqueSizeRangeItemsVMToFieldFab(input: SysqueSizeRangeItemsVM[]): FieldFabConduitSizeRangeVM {
  const sizeRanges: string[] = [];
  const bendRadii: string[] = [];
  const leadLengths: string[] = [];
  const takeUps: string[] = [];

  input.forEach((item) => {
    const diameter = item['Diameter'];
    const bendRadius = item['Bend Radius'];
    const leadLength = item['Lead Length'];
    const takeUp = item['Take Up'];
    const isChecked = item.isChecked;

    sizeRanges.push(isChecked ? `${diameter}` : `${diameter}*`);
    bendRadii.push(isChecked ? `${bendRadius}` : `${bendRadius}*`);
    leadLengths.push(isChecked ? `${leadLength}` : `${leadLength}*`);
    takeUps.push(isChecked ? `${takeUp}` : `${takeUp}*`);
  });

  return new FieldFabConduitSizeRangeVM(
    sizeRanges.join(','),
    bendRadii.join(','),
    leadLengths.join(','),
    takeUps.join(',')
  );
}
//#endregion

//#region JunctionBox

export function getJunctionBoxConnectors(): connectorDescription[] {
  return [
    {
      connector: 'Knockout Male',
      connectsTo: 'Knockout Female',
      notation: 108,
    },
    {
      connector: 'Knockout Female',
      connectsTo: 'Knockout Male',
      notation: 109,
    },
  ];
}

//#endregion

//#region HandAndMachineBend

export function convertHandAndMachineBendStringToSysqueSizeRangeVM(
  input: HandAndMachineBendSizeRangeVM,
  offsetType: string
): SysqueSizeRangeItemsVM[] {
  if (!input.sizeRange || !input.bendRadius || !input.leadLength || !input.leadOutLength || !input.takeUp) {
    return [];
  }

  const isSaddle = offsetType.toLowerCase() !== 'stub machine bend' && offsetType.toLowerCase() !== 'stub hand bend';
  const sizeRanges = input.sizeRange?.split(',');
  
  const bendRadii = input.bendRadius?.split(',');
  const leadLengths = input.leadLength?.split(',');
  const leadOutLengths = input.leadOutLength?.split(',');
  const takeUps = input.takeUp.split(',');

  const sysqueSizeRangeItems: SysqueSizeRangeItemsVM[] = sizeRanges.map((sizeRange, index) => {
    const [diameter1, diameter2] = sizeRange.replace('*', '').split('x').map(Number);
    const isChecked =
      sizeRange.endsWith('*') ||
      bendRadii[index].endsWith('*') ||
      leadLengths[index].endsWith('*') ||
      leadOutLengths[index].endsWith('*') ||
      takeUps[index].endsWith('*')
        ? 0
        : 1;

    const item: SysqueSizeRangeItemsVM = {
      id: index,
      isChecked,
      Diameter1: diameter1,
      Diameter2: diameter2,
      'Bend Radius': parseFloat(bendRadii[index].replace('*', '')),
    };

    if (isSaddle) {
      item['Lead Length'] = parseFloat(leadLengths[index].replace('*', ''));
      item['Lead Out Length'] = parseFloat(leadOutLengths[index].replace('*', ''));
      item['Take Up'] = parseFloat(takeUps[index].replace('*', ''));
    } else {
      item['Minimum Stub Length'] = parseFloat(leadLengths[index].replace('*', ''));
      item['Maximum Tail Length'] = parseFloat(leadOutLengths[index].replace('*', ''));
      item['Deduct'] = parseFloat(takeUps[index].replace('*', ''));
    }

    return item;
  });

  return sysqueSizeRangeItems;
}

export function convertSysqueSizeRangeVMToHandAndMachineBendString(
  input: SysqueSizeRangeItemsVM[],
  offsetType: string
): HandAndMachineBendSizeRangeVM {
  const sizeRanges: string[] = [];
  const bendRadii: string[] = [];
  const leadLengths: string[] = [];
  const leadOutLengths: string[] = [];
  const takeUps: string[] = [];

  input.forEach((item) => {
    const sizeRange = `${item['Diameter1']}x${item['Diameter2']}`;
    sizeRanges.push(item.isChecked ? sizeRange : `${sizeRange}*`);

    bendRadii.push(item.isChecked ? `${item['Bend Radius']}` : `${item['Bend Radius']}*`);

    if (offsetType.toLowerCase() !== 'stub machine bend' && offsetType.toLowerCase() !== 'stub hand bend') {
      leadLengths.push(item.isChecked ? `${item['Lead Length']}` : `${item['Lead Length']}*`);
      leadOutLengths.push(item.isChecked ? `${item['Lead Out Length']}` : `${item['Lead Out Length']}*`);
      takeUps.push(item.isChecked ? `${item['Take Up']}` : `${item['Take Up']}*`);
    } else {
      leadLengths.push(item.isChecked ? `${item['Minimum Stub Length']}` : `${item['Minimum Stub Length']}*`);
      leadOutLengths.push(item.isChecked ? `${item['Maximum Tail Length']}` : `${item['Maximum Tail Length']}*`);
      takeUps.push(item.isChecked ? `${item['Deduct']}` : `${item['Deduct']}*`);
    }
  });

  return new HandAndMachineBendSizeRangeVM(
    sizeRanges.join(','),
    bendRadii.join(','),
    leadLengths.join(','),
    leadOutLengths.join(','),
    takeUps.join(',')
  );
}

//#endregion

//#region KickHandAndMachineBend

export function convertKickHandAndMachineBendStringToSysqueSizeRangeVM(
  input: KickHandAndMachineBendSizeRangeVM
): SysqueSizeRangeItemsVM[] {
  if (!input.sizeRange || !input.bendRadius || !input.minimumStubLength || !input.deduct) {
    return [];
  }

  const sizeRanges = input.sizeRange?.split(',');
  const bendRadii = input.bendRadius?.split(',');
  const minimumStubLengths = input.minimumStubLength?.split(',');
  const deducts = input.deduct.split(',');

  const sysqueSizeRangeItems: SysqueSizeRangeItemsVM[] = sizeRanges.map((sizeRange, index) => {
    const [diameter1, diameter2] = sizeRange.replace('*', '').split('x').map(Number);
    const isChecked =
      sizeRange.endsWith('*') ||
      bendRadii[index].endsWith('*') ||
      minimumStubLengths[index].endsWith('*') ||
      deducts[index].endsWith('*')
        ? 0
        : 1;

    const item: SysqueSizeRangeItemsVM = {
      id: index,
      isChecked,
      Diameter1: diameter1,
      Diameter2: diameter2,
      'Bend Radius': parseFloat(bendRadii[index].replace('*', '')),
      'Minimum Stub Length': parseFloat(minimumStubLengths[index].replace('*', '')),
      Deduct: parseFloat(deducts[index].replace('*', '')),
    };
    return item;
  });
  return sysqueSizeRangeItems;
}

export function convertSysqueSizeRangeItemsVMToKickHandAndMachineBendString(
  input: SysqueSizeRangeItemsVM[]
): KickHandAndMachineBendSizeRangeVM {
  const sizeRanges: string[] = [];
  const bendRadii: string[] = [];
  const minimumStubLengths: string[] = [];
  const deducts: string[] = [];

  input.forEach((item) => {
    const sizeRange = `${item['Diameter1']}x${item['Diameter2']}`;
    sizeRanges.push(item.isChecked ? sizeRange : `${sizeRange}*`);
    bendRadii.push(item.isChecked ? `${item['Bend Radius']}` : `${item['Bend Radius']}*`);
    minimumStubLengths.push(item.isChecked ? `${item['Minimum Stub Length']}` : `${item['Minimum Stub Length']}*`);
    deducts.push(item.isChecked ? `${item['Deduct']}` : `${item['Deduct']}*`);
  });

  return new KickHandAndMachineBendSizeRangeVM(
    sizeRanges.join(','),
    bendRadii.join(','),
    minimumStubLengths.join(','),
    deducts.join(',')
  );
}
//#endregion

//#region User Custom Content

export function getUserCustomContentConnectors(): connectorDescription[] {
  return [
    {
      connector: 'Male Threads',
      connectsTo: 'Female Threads',
      notation: 100,
    },
    {
      connector: 'Female Threads',
      connectsTo: 'Male Threads',
      notation: 101,
    },
    {
      connector: 'Plain',
      connectsTo: 'Bell',
      notation: 106,
    },
    {
      connector: 'Bell',
      connectsTo: 'Plain',
      notation: 107,
    },
    {
      connector: 'Knockout Male',
      connectsTo: 'Knockout Female',
      notation: 108,
    },
    {
      connector: 'Knockout Female',
      connectsTo: 'Knockout Male',
      notation: 109,
    },
  ];
}

//#endregion

//#region Device Equipment Details

export function getDeviceEquipmentConnectors(): connectorDescription[] {
  return [
    {
      connector: 'Male Threads',
      connectsTo: 'Female Threads',
      notation: 100,
    },
    {
      connector: 'Female Threads',
      connectsTo: 'Male Threads',
      notation: 101,
    },
    {
      connector: 'Compression Male',
      connectsTo: 'Compression Female',
      notation: 102,
    },
    {
      connector: 'Compression Female',
      connectsTo: 'Compression Male',
      notation: 103,
    },
    {
      connector: 'PVC Plain',
      connectsTo: 'PVC Socket',
      notation: 104,
    },
    {
      connector: 'PVC Socket',
      connectsTo: 'PVC Plain',
      notation: 105,
    },
    {
      connector: 'Plain',
      connectsTo: 'Bell',
      notation: 106,
    },
    {
      connector: 'Bell',
      connectsTo: 'Plain',
      notation: 107,
    },
    {
      connector: 'Knockout Male',
      connectsTo: 'Knockout Female',
      notation: 108,
    },
    {
      connector: 'Knockout Female',
      connectsTo: 'Knockout Male',
      notation: 109,
    },
    {
      connector: 'Plain End',
      connectsTo: 'Female End',
      notation: 200,
    },
    {
      connector: 'Female End',
      connectsTo: 'Plain End',
      notation: 201,
    },
    {
      connector: 'Flanged End',
      connectsTo: 'Flanged End',
      notation: 202,
    },
  ] as connectorDescription[];
}

//#endregion

export function convertFeetToMm(feet: number): number {
  return feet * 304.8;
}

export function convertMmToFeet(mm: number): number {
  return mm / 304.8;
}

/**
 * Update the line graphics information for the item
 * @param item - Family information item
 * @param lineGraphicsDialogConfig - Line graphics dialog configuration
 * @returns - Updated family information item
 */
export function applyChangesForLineGraphics(
  item: IFamilyInformationItem,
  lineGraphicsDialogConfig: LineGraphicsDialogConfig
): IFamilyInformationItem {
  // Update the line graphics information for the item
  item.linePattern = lineGraphicsDialogConfig.currentPattern;
  item.lineWeight = lineGraphicsDialogConfig.lineWeight.toString();
  item.serviceTypeColor = lineGraphicsDialogConfig.color;

  // Return the updated family information item
  return item;
}

/**
 * Create service type
 * @param item - Family information item
 * @param createServiceTypeDialogConfig - Create service type dialog configuration
 * @returns - The created service type
 */
export function applyCreateServiceTypeChanges(
  item: IFamilyInformationItem,
  createServiceTypeDialogConfig: CreateServiceTypeDialogConfig
): ServiceType {
  item.serviceType = createServiceTypeDialogConfig.serviceTypeName;
  const serviceType: ServiceType = {
    Id: item.serviceTypes.length > 0 ? (Math.max(...item.serviceTypes.map((s) => Number(s.Id))) + 1).toString() : '1',
    ServiceName: item.serviceType,
  };
  item.serviceTypes.push(serviceType);
  item.serviceTypes.sort((a, b) => a.ServiceName.localeCompare(b.ServiceName));
  return serviceType;
}

/**
 * Remove service type from the item
 * @param item - Family information item
 * @returns - True if the service type is removed, false otherwise
 */
export function applyDeleteServiceTypeChanges(item: IFamilyInformationItem): boolean {
  // select the service type to be removed
  const selectedServiceType = item.serviceTypes.find((x) => x.ServiceName === item.serviceType);
  if (selectedServiceType?.ServiceName === '<None>') {
    return false;
  }
  //remove the service type from the list
  item.serviceTypes = item.serviceTypes.filter((serviceType) => serviceType !== selectedServiceType);
  return true;
}

//#region Conversion for color picker

// Example:  -2004932 -> #FF0000
export function convertIntARGBToRGBHex(argb: number): string {
  // Extract the RGB components from the ARGB value
  const r = (argb >> 16) & 0xff; // Extracts the red component
  const g = (argb >> 8) & 0xff; // Extracts the green component
  const b = argb & 0xff; // Extracts the blue component

  // Convert the RGB components to a hex string
  const rgbToHex = (r: number, g: number, b: number): string => {
    // Convert each component to a 2-digit hex value and concatenate them
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0').toUpperCase();
  };

  // Return the hex value
  return rgbToHex(r, g, b);
}

// Convert a RGB hex color string to an ARGB integer
export function convertRGBHexToIntARGB(hex: string): number | null {
  // Ensure the hex string starts with '#'
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  } else {
    return null;
  }

  // Convert 3-digit hex to 6-digit hex if necessary
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Parse the hex string into its RGB components
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Combine the alpha (255) and the RGB values into an ARGB integer
  const a = 255; // Fully opaque
  return (a << 24) | (r << 16) | (g << 8) | b;
}

// convert a RGBA string "RGBA(200, 200, 10, 0.9)" to an ARGB integer
export function convertRGBAStringToIntARGB(color: string): number {
  // Use a regular expression to extract the RGBA components from the string
  const rgbaPattern = /^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})(?:,\s*(0|1|0?\.\d+))?\)$/i;
  const match = rgbaPattern.exec(color);

  if (!match) {
    throw new Error('Invalid RGBA string format');
  }

  // Extract the values from the match
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = match[4] ? parseFloat(match[4]) : 1; // Default alpha to 1 if not provided

  // Ensure the RGB values are within the range of 0-255
  const red = Math.max(0, Math.min(255, r));
  const green = Math.max(0, Math.min(255, g));
  const blue = Math.max(0, Math.min(255, b));

  // Convert alpha to 0-255 range
  const alpha = Math.round(a * 255);

  // Combine the values into a single ARGB integer
  return (alpha << 24) | (red << 16) | (green << 8) | blue;
}

//#endregion
